import Phaser from "phaser";
import { randomChoice } from "./utils";
import { useStore } from "../store";
import { eventBus } from "./eventBus";

const BLOCK_SIZE = 16;
const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const LINES_CLEARED_TO_LEVEL_UP = 5;
const STARTING_TICK_DELAY = 1000;

class ActivePiece extends Phaser.GameObjects.Container {
  constructor(scene, x, y, color) {
    super(scene, 0, 0, []);
    this.logicalX = x;
    this.logicalY = y;
    this.orientation = 0;
    this.rects = [];
    this.color = color;
    this.orientations = I_ORIENTATIONS;
    for (const rect of this.orientations[this.orientation]) {
      this.rects.push(this.addRect(rect[0], rect[1], color));
    }
    this.reposition();
  }

  reposition() {
    this.x = this.logicalX * BLOCK_SIZE;
    this.y = this.logicalY * BLOCK_SIZE;
    for (let i = 0; i < this.rects.length; i++) {
      const rect = this.rects[i];
      rect.setPosition(
        this.orientations[this.orientation][i][0] * BLOCK_SIZE,
        this.orientations[this.orientation][i][1] * BLOCK_SIZE,
      );
    }
  }

  currentLogicalPositions() {
    const positions = [];
    for (const p of this.orientations[this.orientation]) {
      positions.push([p[0] + this.logicalX, p[1] + this.logicalY]);
    }
    return positions;
  }

  nextLogicalTickPosition() {
    const nextLogicalY = this.logicalY + 1;
    const positions = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of this.orientations[this.orientation]) {
      let [blockX, blockY] = p;
      blockX += this.logicalX;
      blockY += nextLogicalY;
      positions.push([blockX, blockY]);
      minX = Math.min(minX, blockX);
      minY = Math.min(minY, blockY);
      maxX = Math.max(maxX, blockX);
      maxY = Math.max(maxY, blockY);
    }
    return { positions, minX, minY, maxX, maxY };
  }

  proposedRotationPosition({ deltaX = 0, deltaY = 0 } = {}) {
    const orientation = (this.orientation + 1) % this.orientations.length;
    const positions = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of this.orientations[orientation]) {
      let [blockX, blockY] = p;
      blockX += this.logicalX;
      blockY += this.logicalY;
      positions.push([blockX, blockY]);
      minX = Math.min(minX, blockX);
      minY = Math.min(minY, blockY);
      maxX = Math.max(maxX, blockX);
      maxY = Math.max(maxY, blockY);
    }
    return { positions, minX, minY, maxX, maxY };
  }

  rotate({ deltaX = 0, deltaY = 0 } = {}) {
    this.orientation = (this.orientation + 1) % this.orientations.length;
    this.logicalX += deltaX;
    this.logicalY += deltaY;
    this.reposition();
  }

  addRect(x, y, color) {
    const rect = new Phaser.GameObjects.Image(
      this.scene,
      x * BLOCK_SIZE,
      y * BLOCK_SIZE,
      `block-${color}`,
    );
    this.add(rect);
    rect.setOrigin(0, 0);
    rect.setDisplaySize(BLOCK_SIZE, BLOCK_SIZE);
    return rect;
  }

  tick() {
    this.logicalY += 1;
    this.reposition();
  }

  proposedPositionsWithDelta({ deltaX, deltaY }) {
    const logicalX = this.logicalX + deltaX;
    const logicalY = this.logicalY + deltaY;
    const positions = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of this.orientations[this.orientation]) {
      let [blockX, blockY] = p;
      blockX += logicalX;
      blockY += logicalY;
      positions.push([blockX, blockY]);
      minX = Math.min(minX, blockX);
      minY = Math.min(minY, blockY);
      maxX = Math.max(maxX, blockX);
      maxY = Math.max(maxY, blockY);
    }
    return { positions, minX, minY, maxX, maxY };
  }

  applyDelta({ deltaX, deltaY }) {
    this.logicalX += deltaX;
    this.logicalY += deltaY;
    this.reposition();
  }
}

class FrozenSet extends Phaser.GameObjects.Container {
  constructor(scene, x, y) {
    super(scene, 0, 0, []);
    this.logicalX = x;
    this.logicalY = y;

    this.initializeGrid();
    scene.add.existing(this);
  }

  initializeGrid() {
    this.grid = [];
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      this.grid.push([]);
      for (let x = 0; x < BOARD_WIDTH; x++) {
        this.grid[y].push(null);
      }
    }
  }

  clearLines(positions) {
    const ys = positions.map(([_, y]) => y);
    // ys near the top first
    const uniqueYs = [...new Set(ys)].sort((a, b) => a - b);

    let clearedLines = 0;
    const changedRects = [];

    for (const y of uniqueYs) {
      if (this.grid[y].every((cell) => cell !== null)) {
        clearedLines++;
        for (let x = 0; x < BOARD_WIDTH; x++) {
          const rect = this.grid[y][x];
          if (rect !== null) {
            const leftSide = x < BOARD_WIDTH / 2;
            const targetX = leftSide ? 0 : BOARD_WIDTH * BLOCK_SIZE;
            const xMult = leftSide ? -1 : 1;
            rect.setDepth(-2);
            const tween = rect.scene.tweens.add({
              targets: rect,
              alpha: { from: 0.8, to: 0 },
              duration: Math.random() * 100 + 350,
              x: rect.x + xMult * (BLOCK_SIZE + Math.random() * 3 * BLOCK_SIZE),
              // xMult * (BLOCK_SIZE * 1 + (Math.random() - 0.5) * BLOCK_SIZE),
              // x: rect.x + (Math.random() - 0.5) * BLOCK_SIZE * 5,
              // y: rect.y + Math.random() * -1 * BLOCK_SIZE * 5,
              ease: "Quad.easeOut",
              delay: Math.random() * 50,
            });
            tween.on("complete", () => {
              rect.destroy();
            });
          }
        }
        this.grid[y] = [];

        for (let y2 = y; y2 > 0; y2--) {
          this.grid[y2] = this.grid[y2 - 1];
          for (let x = 0; x < BOARD_WIDTH; x++) {
            const rect = this.grid[y2][x];
            if (rect === null) {
              continue;
            }
            if (
              rect._toShift === undefined ||
              rect._toShift === null ||
              rect._toShift === 0
            ) {
              rect._toShift = BLOCK_SIZE;
              changedRects.push(rect);
            } else {
              rect._toShift += BLOCK_SIZE;
            }
          }
        }
        this.grid[0] = new Array(BOARD_WIDTH).fill(null);
      }
    }

    for (const rect of changedRects) {
      // rect.y += shift;
      const tween = rect.scene.tweens.add({
        targets: rect,
        y: `+=${rect._toShift}`,
        duration: 125,
        ease: "Quad.easeIn",
      });
      rect._toShift = 0;
      tween.on("complete", () => {
        tween.destroy();
      });
    }

    // it's totally insane, but containers just don't respect depth changes
    // this drives me nuts?? but whatever, this ensures that our disappearing
    // rects are above the existing frozen rects
    this.sort("depth");

    return clearedLines;
  }

  subsumeActivePiece(activePiece) {
    let gameOver = false;
    const positions = activePiece.currentLogicalPositions();
    for (const p of positions) {
      const [x, y] = p;
      if (y < 0) {
        gameOver = true;
      } else if (this.has(x, y)) {
        gameOver = true;
      } else {
        this.addRect(x, y, activePiece.color);
      }
    }
    activePiece.destroy();
    if (gameOver) {
      return { clearedLines: 0, gameOver };
    } else {
      return { clearedLines: this.clearLines(positions), gameOver };
    }
  }

  addRect(x, y, color) {
    const rect = this.scene.add.image(
      x * BLOCK_SIZE,
      y * BLOCK_SIZE,
      `block-${color}`,
    );
    this.add(rect);
    rect.setOrigin(0, 0);
    rect.setDisplaySize(BLOCK_SIZE, BLOCK_SIZE);
    rect.setDepth(-1);
    this.grid[y][x] = rect;
    return rect;
  }

  has(x, y) {
    if (x < 0 || x >= BOARD_WIDTH || y < 0 || y >= BOARD_HEIGHT) {
      return false;
    }
    return this.grid[y][x] !== null;
  }

  collidesWithFrozenSet(positions) {
    return positions.some(([x, y]) => {
      return this.has(x, y);
    });
  }

  simulateDrop(positions) {
    let dy = 0;
    while (true) {
      for (const [x, y] of positions) {
        if (this.has(x, y + dy + 1)) {
          return dy;
        }
        if (y + dy + 1 >= BOARD_HEIGHT) {
          return dy;
        }
      }
      dy++;
    }
  }

  reset() {
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const rect = this.grid[y][x];
        if (rect !== null) {
          rect.destroy();
        }
      }
    }

    this.initializeGrid();
  }
}

class PauseScene extends Phaser.Scene {
  constructor() {
    super({ key: "pause" });
  }

  init(data) {
    this.config = { ...this.defaultConfig, ...data };
    this.midgamePause = this.config.midgamePause;
  }

  fadeOutAndRunCallback(callback) {
    this.bgTween.stop();
    this.bgTween.destroy();
    this.bgTween = this.tweens.add({
      targets: this.bg,
      alpha: 0,
      duration: 200,
      ease: "Quad.easeOut",
    });
    this.bgTween.on("complete", () => {
      callback();
    });
  }

  disappearOnUnpause() {
    this.keys.p.on("down", () => {
      this.fadeOutAndRunCallback(() => {
        useStore.getState().togglePaused();
      });
    });
  }

  create() {
    this.keys = {
      p: this.input.keyboard.addKey("P"),
    };

    this.bg = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.cameras.main.width,
      this.cameras.main.height,
      0x444444,
    );

    this.bg.setAlpha(0);

    this.bgTween = this.tweens.add({
      targets: this.bg,
      alpha: 0.8,
      duration: this.midgamePause ? 200 : 100,
      ease: "Quad.easeIn",
    });

    if (this.midgamePause) {
      this.disappearOnUnpause();
    }
  }
}

class GhostPiece extends Phaser.GameObjects.Container {
  constructor(scene) {
    super(scene, 0, 0, []);

    this.rects = [];

    for (let i = 0; i < 4; i++) {
      const sprite = new Phaser.GameObjects.Image(scene, 0, 0, "ghost");
      this.add(sprite);
      sprite.setOrigin(0, 0);
      sprite.setDisplaySize(BLOCK_SIZE, BLOCK_SIZE);
      this.rects.push(sprite);
    }
    this.setDepth(-1);
  }

  reposition(frozenSet, activePiece) {
    const positions = activePiece.currentLogicalPositions();
    const dy = frozenSet.simulateDrop(positions);

    for (let i = 0; i < this.rects.length; i++) {
      const rect = this.rects[i];
      let [x, y] = positions[i];
      y += dy;
      rect.setPosition(x * BLOCK_SIZE, y * BLOCK_SIZE);
    }
  }
}

class Tetris extends Phaser.Scene {
  constructor() {
    super({ key: "tetris" });
    this.DAS_DELAY = 160;
    this.DAS_RATE = 50;

    this.moveTimer = null;
    this.repeatTimer = null;

    this.canHoldPiece = true;
  }

  preload() {
    if (process.env.NODE_ENV === "development") {
      this.load.setBaseURL("/");
    } else {
      this.load.setBaseURL("/really-easy-tetris/");
    }
    this.load.image("block-blue", "blocks/blue.png");
    this.load.image("block-green", "blocks/green.png");
    this.load.image("block-lightblue", "blocks/yellow.png");
    this.load.image("block-orange", "blocks/yellow.png");
    this.load.image("block-purple", "blocks/purple.png");
    this.load.image("block-red", "blocks/red.png");
    this.load.image("block-yellow", "blocks/yellow.png");
    this.load.image("block-white", "blocks/white.png");

    this.load.image("big", "blocks/big/greenbigblock.png");

    this.load.image("ghost", "blocks/ghost.png");
  }

  timer() {
    useStore.getState().decrementTimer();
  }

  clearTimer() {
    useStore.getState().clearTimer();
  }

  reset() {
    this.activePiece.destroy();
    this.frozenSet.reset();
    this.ghostPiece.destroy();

    const color = randomChoice(COLORS);
    this.activePiece = new ActivePiece(this, BOARD_WIDTH / 2, 0, color);
    this.ghostPiece = new GhostPiece(this);

    this.playfieldContainer.add(this.activePiece);
    this.playfieldContainer.add(this.ghostPiece);

    this.ghostPiece.reposition(this.frozenSet, this.activePiece);

    this.tickTimer.delay = STARTING_TICK_DELAY;

    useStore.getState().clearHeldPiece();
    useStore.getState().clearScore();
    useStore.getState().clearLinesCleared();
    useStore.getState().clearLevel();
    useStore.getState().clearTimer();

    const nextColors = [
      randomChoice(COLORS),
      randomChoice(COLORS),
      randomChoice(COLORS),
    ];
    useStore.getState().setNextPieces(nextColors);

    useStore.getState().setGameOver(false);
    useStore.getState().setPaused(false);
    this.playfieldContainer.sort("depth");
    this.scene.stop("pause");
    this.scene.resume("tetris");
  }

  create() {
    const color = randomChoice(COLORS);
    const playfieldContainer = this.add.container(BLOCK_SIZE / 4, 0, []);
    this.activePiece = new ActivePiece(this, BOARD_WIDTH / 2, 0, color);
    this.frozenSet = new FrozenSet(this, 0, 0);
    this.playfieldContainer = playfieldContainer;
    this.ghostPiece = new GhostPiece(this);

    const playfieldOuterBackground = this.add.rectangle(
      0,
      0,
      BLOCK_SIZE * (BOARD_WIDTH + 0.5),
      BLOCK_SIZE * (BOARD_HEIGHT + 0.25),
      0xc5ccb8,
    );
    playfieldOuterBackground.setOrigin(0, 0);
    playfieldOuterBackground.setDepth(-1);

    const playfieldInnerBackground = this.add.rectangle(
      BLOCK_SIZE / 8,
      0,
      BLOCK_SIZE * (BOARD_WIDTH + 1 / 4),
      BLOCK_SIZE * (BOARD_HEIGHT + 0.125),
      0x6f6776,
    );
    playfieldInnerBackground.setOrigin(0, 0);
    playfieldInnerBackground.setDepth(-1);

    playfieldContainer.add(this.frozenSet);
    playfieldContainer.add(this.activePiece);
    playfieldContainer.add(this.ghostPiece);

    // Setting depth on a container inside another container doesn't seem to
    // trigger a reflow by default, so we do this (which is frustrating)
    playfieldContainer.sort("depth");

    this.ghostPiece.reposition(this.frozenSet, this.activePiece);

    this.keys = {
      r: this.input.keyboard.addKey("R"),
      down: this.input.keyboard.addKey("DOWN"),
      up: this.input.keyboard.addKey("UP"),
      left: this.input.keyboard.addKey("LEFT"),
      right: this.input.keyboard.addKey("RIGHT"),
      space: this.input.keyboard.addKey("SPACE"),
      w: this.input.keyboard.addKey("W"),
      a: this.input.keyboard.addKey("A"),
      s: this.input.keyboard.addKey("S"),
      d: this.input.keyboard.addKey("D"),
      p: this.input.keyboard.addKey("P"),
      h: this.input.keyboard.addKey("H"),
      c: this.input.keyboard.addKey("C"),
      t: this.input.keyboard.addKey("T"),
    };

    this.keys.p.on("down", (e) => {
      useStore.getState().togglePaused();
    });

    this.unsubscribePaused = useStore.subscribe(
      (state) => state.paused,
      (paused, prevPaused) => {
        if (paused && !prevPaused) {
          this.scene.pause();
          this.scene.launch("pause", { midgamePause: true });
        } else if (!paused && prevPaused) {
          this.scene.stop("pause");
          this.scene.resume("tetris");
        }
      },
    );

    this.unsubscribeGameOver = useStore.subscribe(
      (state) => state.gameOver,
      (gameOver, prevGameOver) => {
        if (gameOver && !prevGameOver) {
          this.scene.pause();
          this.scene.launch("pause", { midgamePause: false });
        }
      },
    );

    this.tickTimer = this.time.addEvent({
      delay: STARTING_TICK_DELAY,
      loop: true,
      callback: () => {
        this.tick();
      },
    });

    this.timerTimer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.timer();
        if (useStore.getState().timer <= 0) {
          useStore.getState().setGameOver(true);
        }
      },
    });

    this.eventBusResetLimiter = eventBus.on("reset", () => {
      this.reset();
    });

    this.events.on("shutdown", () => {
      this.cleanup();
    });

    const setupMoveCallback = ({ keys, deltaX, deltaY }) => {
      for (const key of keys) {
        key.on("down", () => {
          this.moveOnceAndStartTimer({ deltaX, deltaY });
        });
        key.on("up", () => {
          this.stopMoveRepeat();
        });
      }
    };

    setupMoveCallback({
      keys: [this.keys.down, this.keys.s],
      deltaX: 0,
      deltaY: 1,
    });

    setupMoveCallback({
      keys: [this.keys.left, this.keys.a],
      deltaX: -1,
      deltaY: 0,
    });

    setupMoveCallback({
      keys: [this.keys.right, this.keys.d],
      deltaX: 1,
      deltaY: 0,
    });

    const nextColors = [
      randomChoice(COLORS),
      randomChoice(COLORS),
      randomChoice(COLORS),
    ];
    useStore.getState().setNextPieces(nextColors);
  }

  cleanup() {
    this.unsubscribePaused && this.unsubscribePaused();
    this.unsubscribeGameOver && this.unsubscribeGameOver();
    this.eventBusResetLimiter &&
      eventBus.off("reset", this.eventBusResetLimiter);
  }

  handleClearedLines(clearedLines) {
    let mult = clearedLines === 4 ? 5 : clearedLines;
    useStore.getState().incrementScore(mult * 100);
    useStore.getState().incrementLinesCleared(clearedLines);
    const linesCleared = useStore.getState().linesCleared;
    if (linesCleared >= LINES_CLEARED_TO_LEVEL_UP) {
      useStore.getState().incrementLevel();
      useStore.getState().decrementLinesCleared(LINES_CLEARED_TO_LEVEL_UP);

      let timerDelay =
        STARTING_TICK_DELAY - (useStore.getState().level - 1) * 200;
      timerDelay = Math.max(timerDelay, 75);
      this.tickTimer.delay = timerDelay;
    }
  }

  subsumeAndReplaceActivePiece() {
    const { clearedLines, gameOver } = this.frozenSet.subsumeActivePiece(
      this.activePiece,
    );

    if (gameOver) {
      useStore.getState().setGameOver(true);
      return;
    }

    if (clearedLines > 0) {
      this.handleClearedLines(clearedLines);
    }

    const nextColor = useStore.getState().nextPieces[0];
    useStore.getState().pushPopNextPiece(randomChoice(COLORS));
    this.canHoldPiece = true;

    this.activePiece = new ActivePiece(this, BOARD_WIDTH / 2, 0, nextColor);
    const positions = this.activePiece.currentLogicalPositions();

    let gameEndedOnSpawn = false;
    for (const p of positions) {
      const [x, y] = p;
      if (this.frozenSet.has(x, y)) {
        gameEndedOnSpawn = true;
      }
    }
    if (gameEndedOnSpawn) {
      this.frozenSet.subsumeActivePiece(this.activePiece);
      useStore.getState().setGameOver(true);
      return;
    }

    this.playfieldContainer.add(this.activePiece);
    this.ghostPiece.reposition(this.frozenSet, this.activePiece);
  }

  tick() {
    const { positions, maxY } = this.activePiece.proposedPositionsWithDelta({
      deltaX: 0,
      deltaY: 1,
    });
    if (maxY >= BOARD_HEIGHT || this.collidesWithFrozenSet(positions)) {
      this.subsumeAndReplaceActivePiece();
    } else {
      this.activePiece.tick();
    }
  }

  isOutOfBounds({ positions, minX, minY, maxX, maxY }) {
    if (minX < 0 || maxX >= BOARD_WIDTH || maxY >= BOARD_HEIGHT) {
      return true;
    }
    return false;
  }

  validateAndApplyDelta({ deltaX, deltaY }) {
    const pos = this.activePiece.proposedPositionsWithDelta({ deltaX, deltaY });
    if (this.isOutOfBounds(pos)) {
      return;
    }
    if (this.collidesWithFrozenSet(pos.positions)) {
      return;
    }
    this.activePiece.applyDelta({ deltaX, deltaY });
    this.ghostPiece.reposition(this.frozenSet, this.activePiece);
  }

  collidesWithFrozenSet(positions) {
    return this.frozenSet.collidesWithFrozenSet(positions);
  }

  validateAndRotate() {
    const pos = this.activePiece.proposedRotationPosition();
    let { positions, minX, minY, maxX, maxY } = pos;
    if (maxY >= BOARD_HEIGHT) {
      return;
    }
    let deltaX = 0;
    if (minX < 0) {
      deltaX = -minX;
    } else if (maxX >= BOARD_WIDTH - 1) {
      deltaX = BOARD_WIDTH - 1 - maxX;
    }

    positions = positions.map(([x, y]) => [x + deltaX, y]);

    if (this.collidesWithFrozenSet(positions)) {
      return;
    }
    this.activePiece.rotate({ deltaX, deltaY: 0 });
    this.ghostPiece.reposition(this.frozenSet, this.activePiece);
  }

  dropToBottom() {
    const positions = this.activePiece.currentLogicalPositions();
    const dy = this.frozenSet.simulateDrop(positions);
    this.activePiece.applyDelta({ deltaX: 0, deltaY: dy });
    this.subsumeAndReplaceActivePiece();
  }

  stopMoveRepeat() {
    if (this.moveTimer) {
      this.moveTimer.destroy();
      this.moveTimer = null;
    }
    if (this.repeatTimer) {
      this.repeatTimer.destroy();
      this.repeatTimer = null;
    }
  }

  moveOnceAndStartTimer({ deltaX, deltaY }) {
    this.stopMoveRepeat();
    this.validateAndApplyDelta({ deltaX, deltaY });

    this.moveTimer = this.time.delayedCall(this.DAS_DELAY, () => {
      this.moveTimer = null;
      this.repeatTimer = this.time.addEvent({
        delay: this.DAS_RATE,
        loop: true,
        callback: () => {
          this.validateAndApplyDelta({ deltaX, deltaY });
        },
      });
    });
  }

  holdPiece() {
    const colorToHold = this.activePiece.color;

    let nextColor = useStore.getState().heldPiece;
    if (nextColor === null) {
      nextColor = useStore.getState().nextPieces[0];
      useStore.getState().pushPopNextPiece(randomChoice(COLORS));
    }

    useStore.getState().setHeldPiece(colorToHold);
    this.activePiece.destroy();
    this.activePiece = new ActivePiece(this, BOARD_WIDTH / 2, 0, nextColor);
    this.playfieldContainer.add(this.activePiece);
    this.ghostPiece.reposition(this.frozenSet, this.activePiece);
    this.canHoldPiece = false;
  }

  update() {
    // rotate on keypress
    const jd = Phaser.Input.Keyboard.JustDown;

    if (jd(this.keys.r) || jd(this.keys.up) || jd(this.keys.w)) {
      this.validateAndRotate();
      return;
    }
    if (jd(this.keys.space)) {
      console.log("PHASER: space pressed");
      this.dropToBottom();
      return;
    }
    if (jd(this.keys.h) || jd(this.keys.c)) {
      if (this.canHoldPiece) {
        this.holdPiece();
        return;
      }
    }
  }
}

const config = {
  type: Phaser.AUTO,
  scale: {
    mode: Phaser.Scale.FIT,
  },
  pixelArt: true,
  width: BLOCK_SIZE * (BOARD_WIDTH + 0.5),
  height: BLOCK_SIZE * (BOARD_HEIGHT + 0.25),
  parent: "phaser-game",
  scene: [Tetris, PauseScene],
  transparent: true,
};

export const createGame = () => {
  return new Phaser.Game(config);
};

const I_ORIENTATIONS = [
  [
    [0, -1],
    [0, 0],
    [0, 1],
    [0, 2],
  ],
  [
    [-1, 0],
    [0, 0],
    [1, 0],
    [2, 0],
  ],
];

const COLORS = ["blue", "green", "purple", "red", "yellow"];
